import { Injectable } from "@angular/core";
import { Effect, ofType, Actions } from "@ngrx/effects";
import { switchMap, catchError, withLatestFrom, mergeMap } from "rxjs/operators";
import { of, zip } from "rxjs";
import {
    EDashboardActions,
    SetInvoices,
    GetBalance,
    SetBalance,
    GetActiveMeters,
    SetActiveMetersElectricity,
    SetActiveMetersGas,
    GetContractRequests,
    SetContractRequests,
    SetUploadReading,
    GetSimulation,
    SetSimulation,
    UploadReading,
    SetFunciona,
    GetFunciona,
    BalanceAPIError,
    ContractRequestsAPIError,
    FuncionaAPIError,
    SetCampaigns,
    GetCampaigns,
    CampaignsAPIError,
    RequestAPIError,
    SetBankList,
    GetMeterInstructions,
    MeterInstructionsAPIError,
    SetMeterInstructions,
    GetElectricMobilityWidgetData,
    ElectricMobilityWidgetAPIError,
    SetElectricMobilityWidgetData,
    InvoicesAPIError
} from "../actions/dashboard.actions";
import { DashboardService } from "@edp-services/dashboard.service";
import { ActiveMeters, Simulation, IMeterTypes } from "@edp-components/edp-card-widgets/edp-widget-consumptions/edp-consumptions.models";
import { ConsumptionsService } from "@edp-services/consumptions.service";
import { ICampaign } from "@edp-models/edp-campaigns.model";
import { IAppState } from "@edp-store/state/app.state";
import { Store } from "@ngrx/store";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { Contract } from "@edp-components/edp-contract";
import { ResetIframe } from "@edp-store/actions/iframe.actions";
import { FlagContractRating } from "@edp-store/actions/contract.actions";
import { GetRatingStatus } from "@edp-store/actions/rating.actions";
import { IReading } from "@edp-models/readings.model";
import { RequestsAndOperations, RequestsAndOperationsFactory } from "@edp-models/requests-and-operations";
import moment from "moment";
import { ElectricMobilityWidget } from "@edp-models/electric-mobility.model";
import { MeddaliaTrackingService } from "@edp-services/meddalia-tracking.service";

@Injectable()
export class DashboardEffects {
    @Effect()
    loadBalance$ = this.actions$.pipe(
        ofType(EDashboardActions.GetBalance),
        switchMap((action: GetBalance) =>
            zip(
                this.dashboardService.getAccountBalance(action.contract.ContractId),
                this.dashboardService.getAccountInvoice(action.contract.ContractId)
            )
        ),
        switchMap((response: any) => {
            const balance = response[0];
            const invoices = response[1];

            if (balance instanceof BalanceAPIError) {
                return of(balance);
            } else {
                let mergedBalance = { ...balance };
                if (balance.balance > 0) {
                    mergedBalance.balance = invoices.balance;
                }
                mergedBalance = {
                    ...mergedBalance,
                    invoices: invoices.invoices,
                    invoicesChart: invoices.invoicesChart,
                    monthlyAverage: invoices.monthlyAverage
                };
                return of(new SetBalance(mergedBalance), new SetInvoices(mergedBalance), new SetBankList(mergedBalance.bankList));
            }
        }),
        catchError(error => {
            return of(new InvoicesAPIError(error), new BalanceAPIError(error));
        })
    );

    @Effect()
    loadContractRequest$ = this.actions$.pipe(
        ofType(EDashboardActions.GetContractRequests),
        switchMap((action: GetContractRequests) =>
            this.dashboardService.getContractRequest(action.contract.ContractId).pipe(
                // switch to error action
                catchError((error: Error) => of(new ContractRequestsAPIError(error)))
            )
        ),
        switchMap((contractRequest: RequestsAndOperations[]) => {
            if (contractRequest instanceof ContractRequestsAPIError) {
                return of(contractRequest);
            } else {
                const contractRequests: RequestsAndOperations[] = [];
                contractRequest.forEach(request => {
                    const requestAndOperation: RequestsAndOperations = RequestsAndOperationsFactory.create(request);
                    contractRequests.push(requestAndOperation);
                });
                return of(new SetContractRequests(contractRequests));
            }
        })
    );

    @Effect()
    loadActiveMeters$ = this.actions$.pipe(
        ofType(EDashboardActions.GetActiveMeters),
        switchMap((action: GetActiveMeters) => {
            return this.consumptionsService.getActiveMeters(action.contract, action.supplyType, action.isLocal).pipe(
                withLatestFrom(this.store.select(getContract)),
                mergeMap(([activeMeters, contract]: [any, Contract]) => {
                    const supplyType = !!action.supplyType
                        ? action.supplyType
                        : this.consumptionsService.getSupplyType(contract);

                    const collection: ActiveMeters[] = [];

                    activeMeters.forEach(el => {
                        const meter = this.consumptionsService.createActiveMeters(el);
                        meter.setActiveMeters(contract);
                        collection.push(meter);
                    });

                    if (supplyType === "E") {
                        return of(new SetActiveMetersElectricity(collection));
                    } else {
                        return of(new SetActiveMetersGas(collection));
                    }
                }),
                catchError((error: Error) => of(new RequestAPIError(error)))
            );
        })
    );

    @Effect()
    loadUploadReading$ = this.actions$.pipe(
        ofType(EDashboardActions.UploadReading),
        switchMap((action: UploadReading) => {
            return this.consumptionsService
                .uploadReading(action.reading)
                .pipe(catchError((error: Error) => of(new RequestAPIError(error))));
        }),
        switchMap((reading: IReading) => {
            if (reading instanceof RequestAPIError) {
                return of(reading);
            } else {
                this.meddaliaTrackingService.setMedalliaTracking("Envio de leituras");
                this.store.dispatch(new FlagContractRating());
                return of(new SetUploadReading(reading), new ResetIframe());
            }
        })
    );

    @Effect()
    loadSimulationReading$ = this.actions$.pipe(
        ofType(EDashboardActions.GetSimulation),
        switchMap((action: GetSimulation) => {
            return this.consumptionsService
                .getSimulation(action.contract.ContractId, action.offline)
                .pipe(catchError((error: Error) => of(new RequestAPIError(error))));
        }),
        switchMap((simulation: Simulation) => {
            const collection = this.consumptionsService.createSimulation(simulation);
            setTimeout(() => {
                this.store.dispatch(new GetRatingStatus());
            }, 4000);

            return of(new SetSimulation(collection));
        })
    );

    @Effect()
    loadFunciona$ = this.actions$.pipe(
        ofType(EDashboardActions.GetFunciona),
        switchMap((action: GetFunciona) => {
            return this.dashboardService
                .getFunciona(action.contract.ContractId)
                .pipe(catchError((error: Error) => of(new FuncionaAPIError(error))));
        }),
        switchMap(funciona => {
            if (funciona instanceof FuncionaAPIError) {
                return of(funciona);
            } else {
                return of(new SetFunciona(funciona));
            }
        })
    );

    @Effect()
    loadDashboardWidget$ = this.actions$.pipe(
        ofType(EDashboardActions.GetCampaigns),
        switchMap((action: GetCampaigns) => {
            return this.dashboardService.getCampaigns(
                action.partnerNumber,
                action.accountNumber)
                .pipe(catchError((error: Error) => of(new CampaignsAPIError(error))));
        }),
        switchMap((campaigns: ICampaign[]) => {
            if (campaigns instanceof CampaignsAPIError) {
                return of(campaigns);
            } else {
                campaigns.forEach(campaign => {
                    campaign.endDate = moment(campaign.endDate)
                        .locale("pt-pt")
                        .format("DD MMM YYYY");
                });
                return of(new SetCampaigns(campaigns));
            }
        })
    );

    @Effect()
    loadMeterInstructions$ = this.actions$.pipe(
        ofType(EDashboardActions.GetMeterInstructions),
        switchMap((action: GetMeterInstructions) => {
            return this.consumptionsService
                .getMeterInstructions()
                .pipe(catchError((error: Error) => of(new MeterInstructionsAPIError(error))));
        }),
        switchMap((meterInstruction: IMeterTypes) => {
            if (meterInstruction instanceof MeterInstructionsAPIError) {
                return of(meterInstruction);
            } else {
                return of(new SetMeterInstructions(meterInstruction));
            }
        })
    );

    @Effect()
    loadElectricMobilityWidget$ = this.actions$.pipe(
        ofType(EDashboardActions.GetElectricMobilityWidgetData),
        switchMap((action: GetElectricMobilityWidgetData) => {
            return this.consumptionsService
                .getElectricMobilityWidgetData(action.contractId)
                .pipe(catchError((error: Error) => of(new ElectricMobilityWidgetAPIError(error))));
        }),
        switchMap((electricMobility: ElectricMobilityWidget) => {
            if (electricMobility instanceof ElectricMobilityWidgetAPIError) {
                return of(electricMobility);
            } else {
                return of(new SetElectricMobilityWidgetData(electricMobility));
            }
        })
    );

    constructor(
        private actions$: Actions,
        private dashboardService: DashboardService,
        private store: Store<IAppState>,
        private consumptionsService: ConsumptionsService,
        private meddaliaTrackingService: MeddaliaTrackingService,
    ) {}
}
